import { useEffect, useRef, useState } from "react";

/**
 * useAmbientAudio — Web Audio API synthesized calming soundscape.
 *
 * Why synthesized? It works offline, no licensing/CDN risk, and lets us
 * generate a slow breathing-paced amplitude modulation that ties the audio
 * to the visual breathing pacer.
 *
 * Layers:
 *   1. Drone pad — two slightly detuned sine oscillators (A2 + E3) feeding
 *      a low-pass filter and a slow LFO on the gain (≈0.1 Hz) so it gently
 *      "breathes".
 *   2. Soft pink-ish noise — a ScriptProcessor or BufferSource, very low
 *      gain, gives the texture of an ambient field.
 *   3. Binaural delta — two pure sines 100 Hz / 104 Hz panned hard L/R for
 *      a 4 Hz delta-wave brainwave entrainment cue. Opt-in.
 */
export default function useAmbientAudio() {
  const [enabled, setEnabled] = useState(false);
  const [binaural, setBinaural] = useState(false);
  const [volume, setVolume] = useState(0.35);

  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const nodesRef = useRef({});

  const ensureContext = () => {
    if (ctxRef.current) return ctxRef.current;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    const ctx = new AC();
    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);
    ctxRef.current = ctx;
    masterRef.current = master;
    return ctx;
  };

  const start = async () => {
    const ctx = ensureContext();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      try {
        await ctx.resume();
      } catch (err) { console.debug("ambient audio resume:", err); }
    }
    if (nodesRef.current.running) return;

    // --- drone pad ----------------------------------------------------------
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 720;
    lp.Q.value = 0.6;

    const padGain = ctx.createGain();
    padGain.gain.value = 0.55;

    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.value = 110; // A2
    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = 164.81; // E3
    const osc3 = ctx.createOscillator();
    osc3.type = "triangle";
    osc3.frequency.value = 220; // A3 (very soft top)
    const osc3Gain = ctx.createGain();
    osc3Gain.gain.value = 0.25;

    osc1.connect(padGain);
    osc2.connect(padGain);
    osc3.connect(osc3Gain).connect(padGain);
    padGain.connect(lp).connect(masterRef.current);

    // breathing LFO on the pad gain (slow, ~0.083 Hz = 12 s cycle)
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 1 / 12;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.25;
    lfo.connect(lfoGain).connect(padGain.gain);

    // --- soft noise bed -----------------------------------------------------
    const noiseBufSize = 2 * ctx.sampleRate;
    const noiseBuf = ctx.createBuffer(1, noiseBufSize, ctx.sampleRate);
    const ch = noiseBuf.getChannelData(0);
    // pink-ish noise via Voss-McCartney approximation (simple version)
    let b0 = 0, b1 = 0, b2 = 0;
    for (let i = 0; i < noiseBufSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99765 * b0 + white * 0.0990460;
      b1 = 0.96300 * b1 + white * 0.2965164;
      b2 = 0.57000 * b2 + white * 1.0526913;
      ch[i] = (b0 + b1 + b2 + white * 0.1848) * 0.06;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    noise.loop = true;
    const noiseLp = ctx.createBiquadFilter();
    noiseLp.type = "lowpass";
    noiseLp.frequency.value = 1100;
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.08;
    noise.connect(noiseLp).connect(noiseGain).connect(masterRef.current);

    // --- binaural delta (opt-in) -------------------------------------------
    let binL, binR, binMerger, binGain;
    if (binaural) {
      binL = ctx.createOscillator();
      binL.frequency.value = 100;
      binR = ctx.createOscillator();
      binR.frequency.value = 104; // 4 Hz delta
      binMerger = ctx.createChannelMerger(2);
      binL.connect(binMerger, 0, 0);
      binR.connect(binMerger, 0, 1);
      binGain = ctx.createGain();
      binGain.gain.value = 0.08;
      binMerger.connect(binGain).connect(masterRef.current);
      binL.start();
      binR.start();
    }

    osc1.start();
    osc2.start();
    osc3.start();
    lfo.start();
    noise.start();

    // fade-in master
    const now = ctx.currentTime;
    masterRef.current.gain.cancelScheduledValues(now);
    masterRef.current.gain.setValueAtTime(0, now);
    masterRef.current.gain.linearRampToValueAtTime(volume, now + 2.5);

    nodesRef.current = {
      running: true,
      osc1, osc2, osc3, lfo, noise, binL, binR, binMerger, binGain, padGain,
    };
  };

  const stop = () => {
    const ctx = ctxRef.current;
    if (!ctx || !nodesRef.current.running) return;
    const now = ctx.currentTime;
    masterRef.current.gain.cancelScheduledValues(now);
    masterRef.current.gain.setValueAtTime(masterRef.current.gain.value, now);
    masterRef.current.gain.linearRampToValueAtTime(0, now + 1.2);
    const n = nodesRef.current;
    setTimeout(() => {
      try { n.osc1?.stop(); } catch (err) { console.debug("ambient osc1 stop:", err); }
      try { n.osc2?.stop(); } catch (err) { console.debug("ambient osc2 stop:", err); }
      try { n.osc3?.stop(); } catch (err) { console.debug("ambient osc3 stop:", err); }
      try { n.lfo?.stop(); } catch (err) { console.debug("ambient lfo stop:", err); }
      try { n.noise?.stop(); } catch (err) { console.debug("ambient noise stop:", err); }
      try { n.binL?.stop(); } catch (err) { console.debug("ambient binL stop:", err); }
      try { n.binR?.stop(); } catch (err) { console.debug("ambient binR stop:", err); }
      nodesRef.current = {};
    }, 1500);
  };

  // react to volume changes
  useEffect(() => {
    if (!masterRef.current || !ctxRef.current) return;
    if (!nodesRef.current.running) return;
    const ctx = ctxRef.current;
    const now = ctx.currentTime;
    masterRef.current.gain.cancelScheduledValues(now);
    masterRef.current.gain.linearRampToValueAtTime(volume, now + 0.4);
  }, [volume]);

  // toggle on/off
  useEffect(() => {
    if (enabled) start();
    else stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // restart when binaural toggle flips (so we add/remove that layer cleanly)
  useEffect(() => {
    if (!enabled) return;
    stop();
    const t = setTimeout(() => start(), 1600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [binaural]);

  useEffect(() => () => stop(), []); // unmount

  return {
    enabled,
    setEnabled,
    binaural,
    setBinaural,
    volume,
    setVolume,
  };
}
